import _toConsumableArray from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _createForOfIteratorHelper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _objectSpread from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _classCallCheck from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _get from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/get";
import _getPrototypeOf from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import { Plugin } from 'ckeditor5/src/core.js';
import { findOptimalInsertionRange, isWidget, toWidget } from 'ckeditor5/src/widget.js';
import { determineImageTypeForInsertionAtSelection } from './image/utils.js';
import { DomEmitterMixin, global } from 'ckeditor5/src/utils.js';
var IMAGE_WIDGETS_CLASSES_MATCH_REGEXP = /^(image|image-inline)$/;
/**
 * A set of helpers related to images.
 */
var ImageUtils = /*#__PURE__*/function (_Plugin) {
  _inherits(ImageUtils, _Plugin);
  var _super = _createSuper(ImageUtils);
  function ImageUtils() {
    var _this;
    _classCallCheck(this, ImageUtils);
    _this = _super.apply(this, arguments);
    /**
     * DOM Emitter.
     */
    _this._domEmitter = new (DomEmitterMixin())();
    return _this;
  }
  /**
   * @inheritDoc
   */
  _createClass(ImageUtils, [{
    key: "isImage",
    value:
    /**
     * Checks if the provided model element is an `image` or `imageInline`.
     */
    function isImage(modelElement) {
      return this.isInlineImage(modelElement) || this.isBlockImage(modelElement);
    }
    /**
     * Checks if the provided view element represents an inline image.
     *
     * Also, see {@link module:image/imageutils~ImageUtils#isImageWidget}.
     */
  }, {
    key: "isInlineImageView",
    value: function isInlineImageView(element) {
      return !!element && element.is('element', 'img');
    }
    /**
     * Checks if the provided view element represents a block image.
     *
     * Also, see {@link module:image/imageutils~ImageUtils#isImageWidget}.
     */
  }, {
    key: "isBlockImageView",
    value: function isBlockImageView(element) {
      return !!element && element.is('element', 'figure') && element.hasClass('image');
    }
    /**
     * Handles inserting single file. This method unifies image insertion using {@link module:widget/utils~findOptimalInsertionRange}
     * method.
     *
     * ```ts
     * const imageUtils = editor.plugins.get( 'ImageUtils' );
     *
     * imageUtils.insertImage( { src: 'path/to/image.jpg' } );
     * ```
     *
     * @param attributes Attributes of the inserted image.
     * This method filters out the attributes which are disallowed by the {@link module:engine/model/schema~Schema}.
     * @param selectable Place to insert the image. If not specified,
     * the {@link module:widget/utils~findOptimalInsertionRange} logic will be applied for the block images
     * and `model.document.selection` for the inline images.
     *
     * **Note**: If `selectable` is passed, this helper will not be able to set selection attributes (such as `linkHref`)
     * and apply them to the new image. In this case, make sure all selection attributes are passed in `attributes`.
     *
     * @param imageType Image type of inserted image. If not specified,
     * it will be determined automatically depending of editor config or place of the insertion.
     * @param options.setImageSizes Specifies whether the image `width` and `height` attributes should be set automatically.
     * The default is `true`.
     * @return The inserted model image element.
     */
  }, {
    key: "insertImage",
    value: function insertImage() {
      var _this2 = this;
      var attributes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var selectable = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      var imageType = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      var editor = this.editor;
      var model = editor.model;
      var selection = model.document.selection;
      var determinedImageType = determineImageTypeForInsertion(editor, selectable || selection, imageType);
      // Mix declarative attributes with selection attributes because the new image should "inherit"
      // the latter for best UX. For instance, inline images inserted into existing links
      // should not split them. To do that, they need to have "linkHref" inherited from the selection.
      attributes = _objectSpread(_objectSpread({}, Object.fromEntries(selection.getAttributes())), attributes);
      for (var attributeName in attributes) {
        if (!model.schema.checkAttribute(determinedImageType, attributeName)) {
          delete attributes[attributeName];
        }
      }
      return model.change(function (writer) {
        var _options$setImageSize = options.setImageSizes,
          setImageSizes = _options$setImageSize === void 0 ? true : _options$setImageSize;
        var imageElement = writer.createElement(determinedImageType, attributes);
        model.insertObject(imageElement, selectable, null, {
          setSelection: 'on',
          // If we want to insert a block image (for whatever reason) then we don't want to split text blocks.
          // This applies only when we don't have the selectable specified (i.e., we insert multiple block images at once).
          findOptimalPosition: !selectable && determinedImageType != 'imageInline' ? 'auto' : undefined
        });
        // Inserting an image might've failed due to schema regulations.
        if (imageElement.parent) {
          if (setImageSizes) {
            _this2.setImageNaturalSizeAttributes(imageElement);
          }
          return imageElement;
        }
        return null;
      });
    }
    /**
     * Reads original image sizes and sets them as `width` and `height`.
     *
     * The `src` attribute may not be available if the user is using an upload adapter. In such a case,
     * this method is called again after the upload process is complete and the `src` attribute is available.
     */
  }, {
    key: "setImageNaturalSizeAttributes",
    value: function setImageNaturalSizeAttributes(imageElement) {
      var _this3 = this;
      var src = imageElement.getAttribute('src');
      if (!src) {
        return;
      }
      if (imageElement.getAttribute('width') || imageElement.getAttribute('height')) {
        return;
      }
      this.editor.model.change(function (writer) {
        var img = new global.window.Image();
        _this3._domEmitter.listenTo(img, 'load', function () {
          if (!imageElement.getAttribute('width') && !imageElement.getAttribute('height')) {
            // We use writer.batch to be able to undo (in a single step) width and height setting
            // along with any change that triggered this action (e.g. image resize or image style change).
            _this3.editor.model.enqueueChange(writer.batch, function (writer) {
              writer.setAttribute('width', img.naturalWidth, imageElement);
              writer.setAttribute('height', img.naturalHeight, imageElement);
            });
          }
          _this3._domEmitter.stopListening(img, 'load');
        });
        img.src = src;
      });
    }
    /**
     * Returns an image widget editing view element if one is selected or is among the selection's ancestors.
     */
  }, {
    key: "getClosestSelectedImageWidget",
    value: function getClosestSelectedImageWidget(selection) {
      var selectionPosition = selection.getFirstPosition();
      if (!selectionPosition) {
        return null;
      }
      var viewElement = selection.getSelectedElement();
      if (viewElement && this.isImageWidget(viewElement)) {
        return viewElement;
      }
      var parent = selectionPosition.parent;
      while (parent) {
        if (parent.is('element') && this.isImageWidget(parent)) {
          return parent;
        }
        parent = parent.parent;
      }
      return null;
    }
    /**
     * Returns a image model element if one is selected or is among the selection's ancestors.
     */
  }, {
    key: "getClosestSelectedImageElement",
    value: function getClosestSelectedImageElement(selection) {
      var selectedElement = selection.getSelectedElement();
      return this.isImage(selectedElement) ? selectedElement : selection.getFirstPosition().findAncestor('imageBlock');
    }
    /**
     * Returns an image widget editing view based on the passed image view.
     */
  }, {
    key: "getImageWidgetFromImageView",
    value: function getImageWidgetFromImageView(imageView) {
      return imageView.findAncestor({
        classes: IMAGE_WIDGETS_CLASSES_MATCH_REGEXP
      });
    }
    /**
     * Checks if image can be inserted at current model selection.
     *
     * @internal
     */
  }, {
    key: "isImageAllowed",
    value: function isImageAllowed() {
      var model = this.editor.model;
      var selection = model.document.selection;
      return isImageAllowedInParent(this.editor, selection) && isNotInsideImage(selection);
    }
    /**
     * Converts a given {@link module:engine/view/element~Element} to an image widget:
     * * Adds a {@link module:engine/view/element~Element#_setCustomProperty custom property} allowing to recognize the image widget
     * element.
     * * Calls the {@link module:widget/utils~toWidget} function with the proper element's label creator.
     *
     * @param writer An instance of the view writer.
     * @param label The element's label. It will be concatenated with the image `alt` attribute if one is present.
     */
  }, {
    key: "toImageWidget",
    value: function toImageWidget(viewElement, writer, label) {
      var _this4 = this;
      writer.setCustomProperty('image', true, viewElement);
      var labelCreator = function labelCreator() {
        var imgElement = _this4.findViewImgElement(viewElement);
        var altText = imgElement.getAttribute('alt');
        return altText ? "".concat(altText, " ").concat(label) : label;
      };
      return toWidget(viewElement, writer, {
        label: labelCreator
      });
    }
    /**
     * Checks if a given view element is an image widget.
     */
  }, {
    key: "isImageWidget",
    value: function isImageWidget(viewElement) {
      return !!viewElement.getCustomProperty('image') && isWidget(viewElement);
    }
    /**
     * Checks if the provided model element is an `image`.
     */
  }, {
    key: "isBlockImage",
    value: function isBlockImage(modelElement) {
      return !!modelElement && modelElement.is('element', 'imageBlock');
    }
    /**
     * Checks if the provided model element is an `imageInline`.
     */
  }, {
    key: "isInlineImage",
    value: function isInlineImage(modelElement) {
      return !!modelElement && modelElement.is('element', 'imageInline');
    }
    /**
     * Get the view `<img>` from another view element, e.g. a widget (`<figure class="image">`), a link (`<a>`).
     *
     * The `<img>` can be located deep in other elements, so this helper performs a deep tree search.
     */
  }, {
    key: "findViewImgElement",
    value: function findViewImgElement(figureView) {
      if (this.isInlineImageView(figureView)) {
        return figureView;
      }
      var editingView = this.editor.editing.view;
      var _iterator = _createForOfIteratorHelper(editingView.createRangeIn(figureView)),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var item = _step.value.item;
          if (this.isInlineImageView(item)) {
            return item;
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }
    /**
     * @inheritDoc
     */
  }, {
    key: "destroy",
    value: function destroy() {
      this._domEmitter.stopListening();
      return _get(_getPrototypeOf(ImageUtils.prototype), "destroy", this).call(this);
    }
  }], [{
    key: "pluginName",
    get: function get() {
      return 'ImageUtils';
    }
  }]);
  return ImageUtils;
}(Plugin);
/**
 * Checks if image is allowed by schema in optimal insertion parent.
 */
export { ImageUtils as default };
function isImageAllowedInParent(editor, selection) {
  var imageType = determineImageTypeForInsertion(editor, selection, null);
  if (imageType == 'imageBlock') {
    var parent = getInsertImageParent(selection, editor.model);
    if (editor.model.schema.checkChild(parent, 'imageBlock')) {
      return true;
    }
  } else if (editor.model.schema.checkChild(selection.focus, 'imageInline')) {
    return true;
  }
  return false;
}
/**
 * Checks if selection is not placed inside an image (e.g. its caption).
 */
function isNotInsideImage(selection) {
  return _toConsumableArray(selection.focus.getAncestors()).every(function (ancestor) {
    return !ancestor.is('element', 'imageBlock');
  });
}
/**
 * Returns a node that will be used to insert image with `model.insertContent`.
 */
function getInsertImageParent(selection, model) {
  var insertionRange = findOptimalInsertionRange(selection, model);
  var parent = insertionRange.start.parent;
  if (parent.isEmpty && !parent.is('element', '$root')) {
    return parent.parent;
  }
  return parent;
}
/**
 * Determine image element type name depending on editor config or place of insertion.
 *
 * @param imageType Image element type name. Used to force return of provided element name,
 * but only if there is proper plugin enabled.
 */
function determineImageTypeForInsertion(editor, selectable, imageType) {
  var schema = editor.model.schema;
  var configImageInsertType = editor.config.get('image.insert.type');
  if (!editor.plugins.has('ImageBlockEditing')) {
    return 'imageInline';
  }
  if (!editor.plugins.has('ImageInlineEditing')) {
    return 'imageBlock';
  }
  if (imageType) {
    return imageType;
  }
  if (configImageInsertType === 'inline') {
    return 'imageInline';
  }
  if (configImageInsertType !== 'auto') {
    return 'imageBlock';
  }
  // Try to replace the selected widget (e.g. another image).
  if (selectable.is('selection')) {
    return determineImageTypeForInsertionAtSelection(schema, selectable);
  }
  return schema.checkChild(selectable, 'imageInline') ? 'imageInline' : 'imageBlock';
}